import type { QueryClient, QueryKey } from "@tanstack/react-query";
import { hrGet } from "../../api/hrApi";

/**
 * Small plumbing shared by every HR screen hook (dashboard + attendance).
 * The HTTP layer itself is `api/hrApi.ts`; this only adds what React Query
 * needs on top of it.
 */

export type HrParams = Record<string, string | number | boolean | null | undefined>;

/**
 * A response stamped with the BROWSER time it arrived.
 *
 * Running sessions and the punch header tick from "elapsed since this page
 * was generated". Measuring that against the browser's own receive time (not
 * the server's `generatedAt`) keeps a skewed laptop clock out of the maths,
 * and stamping per PAGE matters: pages of an infinite list arrive minutes
 * apart, so one query-level `dataUpdatedAt` would under-count older pages.
 */
export type Stamped<T> = T & { receivedAt: number };

export async function hrGetStamped<T>(path: string, params?: HrParams): Promise<Stamped<T>> {
  const data = await hrGet<T>(path, params);
  return { ...((data ?? {}) as object), receivedAt: Date.now() } as Stamped<T>;
}

/** Whole minutes elapsed since `receivedAt` (never negative). */
export function minutesSince(receivedAt: number | undefined, now: number): number {
  if (!receivedAt) return 0;
  return Math.max(0, Math.floor((now - receivedAt) / 60_000));
}

/** Infinite-query page param: `null` for page 1, then the server's opaque cursor. */
export type HrCursor = string | null;

/**
 * Writes `value` into a query ONLY if that query has no data yet.
 *
 * The dashboard's one aggregate call already carries page 1 of every list;
 * seeding those pages into the lists' own caches means the widgets never
 * re-request what just arrived, while a list that already holds more pages
 * (or newer data) is never clobbered by the aggregate's copy.
 */
export function seedIfEmpty(qc: QueryClient, key: QueryKey, value: unknown) {
  if (value === undefined) return;
  if (qc.getQueryData(key) !== undefined) return;
  qc.setQueryData(key, value);
}

/**
 * A fresh idempotency key per click (UI contract §1.2). `crypto.randomUUID`
 * only exists in secure contexts; the fallback builds an RFC-4122 v4 id from
 * `getRandomValues`, which is available everywhere the app runs.
 */
export function newIdempotencyKey(): string {
  const c: Crypto | undefined = typeof crypto !== "undefined" ? crypto : undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  const bytes = new Uint8Array(16);
  if (c?.getRandomValues) c.getRandomValues(bytes);
  else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Reads a Blob error body (CSV downloads use responseType "blob"). */
export async function blobErrorMessage(err: unknown, fallback: string): Promise<string> {
  const data = (err as any)?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      if (parsed?.message) return String(parsed.message);
    } catch {
      /* not JSON — fall through */
    }
  }
  return (err as any)?.response?.data?.message || (err as any)?.message || fallback;
}
